import * as React from "react";
import { connect, useSelector } from "react-redux";
import SplitPane from "react-split-pane";
import { debounce } from "lodash";
import styled, { css } from "styled-components";
import theme from "styled-theming";

import { ThemeType } from "shared/styles/theme";
import * as S from "@styled";
import colors from "shared/styles/colors";
import CustomModal, { IModalProps, StyledCloseButton } from "shared/components/CustomModal";
import { thumbUpIcon, thumbDownIcon, frendsIcon, sidePanelIcon } from "shared/icons/svgHelpers";
import { InlineLoadSpinner } from "shared/components/PageLoadSpinner";
import MonacoEditor from "shared/monaco/components/MonacoEditor";
import { DispatchType, Utilities } from "shared/utilities";
import useAzureAiContextGather, { ITaskCompletionData, IUseAzureAiContextGather, useTaskCompletionDataGather } from "processEditor/hooks/UseAzureAiContextGather";
import useCtrlCopyInterceptor from "processEditor/hooks/UseCtrlCopyInterceptor";
import { setInitialParameters, updateParameter } from "processEditor/actions/UpdateParameter";
import { State } from "processEditor/model/State";
import { ParameterEditorMode } from "processEditor/components/ParameterInput";
import { IParameterSchema, IParameters } from "processEditor/model/IProcessData";
import ComplexParameter from "../ComplexParameter";
import useAzureAiSignalR from "processEditor/hooks/UseAzureAiSignalR";
import { MultiSelectHideOverFlow } from "shared/components/select/Select";
import { Option } from "shared/model/Option";
import { AiMarkdownResponse } from "../Documentation/DocumentationEditor";

const AssistantModal: React.FC<AssistantModalProps> = (props) => {
    // the genereic typing for the hook is not very very useful here as we can't know design time which one it is when the code is shared
    // will be usefull for ducumentation AI
    const { completionMessage, errorMessage, completionInProgress, startSession, promptCompletion } = useAzureAiSignalR<string | TaskAiResponse | null>(`${Utilities.getUrlBase()}azureAi`, props.type);
    const sessionId = React.useRef(crypto.randomUUID());
    const completionSessionStarted = React.useRef(false);

    const [chatMessages, setChatMessages] = React.useState<ChatMessage[]>([]);
    const messagesArticleRef = React.useRef<HTMLElement>(null);
    const userInputRef = React.useRef<HTMLTextAreaElement>(null);
    const sendButtonRef = React.useRef<HTMLButtonElement>(null);

    const initialContext = useAzureAiContextGather(props.elementId, props.type);
    const [contextPanelOpen, setContextPanelOpen] = React.useState(false);
    const [contextInputValue, setContextInputValue] = React.useState('');
    const [parsedContext, setParsedContext] = React.useState<ParsedContext>({});
    const allEnvironmentVariables = useSelector<State, Option[]>(state => state.environmentVariables
        .filter(v => v.includes("."))
        .map(v => ({ label: `#env.${v}`, value: `#env.${v}` })));

    const [selectedEnvironmentVariables, setSelectedEnvironmentVariables] = React.useState<string[]>([]);

    // Prevent bpmn.js/keyboard.js from handling the CTRL + c events and allow copy from the modal
    useCtrlCopyInterceptor();

    React.useEffect(() => {
        const articleCurrent = messagesArticleRef.current;
        if (articleCurrent && articleCurrent.clientHeight < articleCurrent.scrollHeight) {
            articleCurrent.scrollTop = articleCurrent.scrollHeight;
        }
    }, [chatMessages]);

    const getNextChatMessages = (newMessage: ChatMessage, replaceAt?: number) => {
        if (replaceAt !== undefined && 0 <= replaceAt && replaceAt < chatMessages.length) {
            return chatMessages.with(replaceAt, newMessage);
        }
        return [...chatMessages, newMessage];
    };

    React.useEffect(() => {
        if (chatMessages.length > 0 && completionMessage !== '' && completionMessage != null) {
            const latestMsgIdx = chatMessages.length - 1;
            const isLatestMessageFromAssistant = chatMessages[latestMsgIdx].role === ChatMessageRole.Assistant;
            const replaceAtIdx = isLatestMessageFromAssistant ? latestMsgIdx : undefined;

            setChatMessages(getNextChatMessages({
                message: completionMessage,
                role: ChatMessageRole.Assistant,
            }, replaceAtIdx));
        }

        setSendButtonDisabled();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [completionMessage, completionInProgress]);

    const getUserInputValue = () => userInputRef.current?.value ?? '';

    const setSendButtonDisabled = (inputValue?: string, context?: ParsedContext) => {
        if (sendButtonRef.current) {
            const disabled = completionInProgress || (inputValue ?? getUserInputValue()).trim() === '' || !!(context ?? parsedContext).error;
            sendButtonRef.current.disabled = disabled;
        }
    };

    const handleContextInputChange = (context: string) => {
        setContextInputValue(context);

        if (context === '') {
            setParsedContext({});
            setSendButtonDisabled(undefined, {});
            return;
        }

        let nextParsedContext: ParsedContext;
        try {
            nextParsedContext = { context: JSON.parse(context) };
            setSelectedEnvironmentVariables(nextParsedContext.context?.environmentVariables ?? []);
        } catch (e) {
            setSelectedEnvironmentVariables([]);
            nextParsedContext = { error: e.message };
        }
        // Clear selected environment variables if data edited manually as the list won't match anymore

        setParsedContext(nextParsedContext);
        setSendButtonDisabled(undefined, nextParsedContext);
    };

    const handleSelectedEnvironmentVariablesChange = (environmentVariables: Option[]) => {
        const selectedEnvVars: string[] = environmentVariables.map((key) => key.value);
        // Ensure parsedContext.context exists before proceeding to avoid issues with undefined
        const updatedContext: ParsedContext = {
            ...parsedContext, // Spread 
            context: parsedContext.context ? {
                ...parsedContext.context, // Spread
                environmentVariables: selectedEnvVars,
            } : undefined
        };
        handleContextInputChange(JSON.stringify(updatedContext.context, null, 4));
    }

    React.useEffect(() => {
        handleContextInputChange(JSON.stringify(initialContext, null, 4));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialContext]);

    const togglContextPanelOpen = () => setContextPanelOpen((prev) => !prev);

    const handleClose = () => props.onClose?.();

    const completionStoppedAndIsLastChatMessage = (msgIdx: number) => !completionInProgress && msgIdx === chatMessages.length - 1;

    const isConversationOngoing = () => chatMessages.length >= 2;

    const getLatestMessageForRole = <T extends ChatMessage>(role: ChatMessageRole): T | undefined => {
        if (isConversationOngoing()) {
            const idxOffset = role === ChatMessageRole.Assistant ? 1 : 2
            const msg = chatMessages[chatMessages.length - idxOffset];
            return msg.role === role ? (msg as T) : undefined;
        }
    };

    const getDiscardAssistantMessageHandler = (msgIdx: number) => {
        if (!completionStoppedAndIsLastChatMessage(msgIdx)) {
            return;
        }

        const latestAssistantMsg = getLatestMessageForRole<AssistantChatMessage>(ChatMessageRole.Assistant);
        const latestUserMsg = getLatestMessageForRole<UserChatMessage>(ChatMessageRole.User);
        if (latestAssistantMsg && latestUserMsg) {
            return () => {
                setChatMessages((prev) => prev.slice(0, chatMessages.length - 2));
                handleUserInputChange(latestUserMsg.message);
                userInputRef.current?.focus();
            };
        }
    };

    const isCSharpCodeBlock = (str: string) => str.trim().startsWith('```csharp') && str.trim().endsWith('```');

    const cleanCSharpCodeBlocks = (str: string) => {
        const regex: RegExp = /```(?:(?:\w+|c#))?\s*([\s\S]*?)\s*```/g;
        return str.replace(regex, (_match: string, codeContent: string) => codeContent.trim());
    };

    const getUseAssistantMessageHandler = (msgIdx: number) => {
        if (!completionStoppedAndIsLastChatMessage(msgIdx)) {
            return;
        }

        const latestAssistantMsg = getLatestMessageForRole<AssistantChatMessage>(ChatMessageRole.Assistant)?.message || '';
        if (props.type == PromptType.CodeCompletion && isCSharpCodeBlock(latestAssistantMsg as string)) {
            return () => {
                const cleanedMsg = cleanCSharpCodeBlocks(latestAssistantMsg as string);
                const indentedMsg = cleanedMsg.replace(/^/gm, '    ');
                props.onUseCodeShape(
                    '{\n' +
                    indentedMsg +
                    '\n}'
                );
                handleClose();
            };
        }

        if (props.type == PromptType.TaskCompletion && (latestAssistantMsg as TaskAiResponse)?.parameterJson != null) {
            return () => {
                props.onUseTaskConfiguration((latestAssistantMsg as TaskAiResponse)?.parameterJson);
                handleClose();
            };
        }

        return () => {
            handleClose();
        };
    };

    const setUserInputValue = (value: string) => {
        if (userInputRef.current) {
            userInputRef.current.value = value;
        }
    };

    const handleUserInputKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSendUserInput();
        }
    };

    const getUserInputLineCount = () => {
        const count = getUserInputValue().split(/\r\n|\r|\n/).length;
        return (count === 1 || count <= 10) ? count : 10;
    };

    const setUserInputRows = () => {
        if (userInputRef.current) {
            userInputRef.current.rows = getUserInputLineCount();
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debounceSetUserInputRows = React.useCallback(
        debounce(() => setUserInputRows(), 500),
        []
    );

    const handleUserInputKeyUp = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if ((event.key === 'Enter' && event.shiftKey) || event.key === 'Backspace' || event.key === 'Delete') {
            setUserInputRows();
        } else {
            debounceSetUserInputRows();
        }
        setSendButtonDisabled();
    };

    const handleUserInputChange = (value: string) => {
        setUserInputValue(value);
        setUserInputRows();
        setSendButtonDisabled();
    };

    const handleSendUserInput = async () => {
        if (sendButtonRef.current?.disabled === false) {
            const nextMessages = getNextChatMessages({
                message: getUserInputValue().trim(),
                role: ChatMessageRole.User,
            });
            setChatMessages(nextMessages);

            handleUserInputChange('');

            if (!completionSessionStarted.current) {
                try {
                    await startSession(sessionId.current);
                } catch (_) {
                    Utilities.toastError('Failed to start completion. Please try again. If the problem persists, please contact support@frends.com');
                }
                // signalR connection will try to reconnect automatically
                completionSessionStarted.current = true;
            }

            try {
                await promptCompletion(sessionId.current, props.type, parsedContext.context, nextMessages);
            } catch (e) {
                Utilities.toastError('Failed to send completion request. Please try again. If the problem persists, please contact support@frends.com');
            }
        }
    };

    const handleCloseContextPanelClick = () => setContextPanelOpen(false);

    return (
        <CustomModal
            show={props.show}
            onClose={handleClose}
            label="bpmn-frends-assistant"
            size="large"
            shouldCloseOnOverlayClick={false}
            modalTitle={
                <StyledModalTitleContainer>
                    <h2>Frends assistant</h2>
                    <StyledOpenContextPanelButton
                        title={contextPanelOpen ? 'Close context panel' : 'Open context panel'}
                        dangerouslySetInnerHTML={{ __html: sidePanelIcon() }}
                        onClick={togglContextPanelOpen}
                        disabled={!!parsedContext.error}
                    />
                </StyledModalTitleContainer>
            }
        >
            <StyledContentContainer>
                {/* @ts-expect-error: ACC-8426: react-split-pane has not updated their types to include React.PropsWithChildren which is required by React 18 */}
                <SplitPane
                    split="vertical"
                    primary="second"
                    minSize={contextPanelOpen ? 250 : 0}
                    defaultSize={contextPanelOpen ? 501 : 0}
                    maxSize={contextPanelOpen ? 1200 : 0}
                    allowResize={contextPanelOpen}
                >
                    <StyledMessagesAndInputPanel contextPanelOpen={contextPanelOpen}>
                        {chatMessages.length === 0 ? (
                            <WelcomeMessageContainer>
                                <span dangerouslySetInnerHTML={{ __html: frendsIcon() }} />
                                <p>Ready to help you!</p>
                            </WelcomeMessageContainer>
                        ) : (
                            <StyledChatMessagesContainer ref={messagesArticleRef}>
                                {chatMessages.map((msg, idx) => (
                                    <React.Fragment key={idx}>
                                        {msg.role === ChatMessageRole.User ? (
                                            <StyledUserMessage key={idx}>
                                                {msg.message}
                                            </StyledUserMessage>
                                        ) : (
                                            <AssistantMessage
                                                sessionId={sessionId.current}
                                                message={msg.message}
                                                error={errorMessage}
                                                type={props.type}
                                                elementId={props.elementId}
                                                completionInProgress={completionInProgress}
                                                onDiscardClick={getDiscardAssistantMessageHandler(idx)}
                                                onUseClick={getUseAssistantMessageHandler(idx)}
                                            />
                                        )}
                                    </React.Fragment>
                                ))}

                                {completionInProgress && (
                                    <InlineLoadSpinner top={0} height={40} width={40} />
                                )}
                            </StyledChatMessagesContainer>
                        )}

                        <StyledUserInputContainer>
                            <S.TextArea
                                ref={userInputRef}
                                onKeyDown={handleUserInputKeyDown}
                                onKeyUp={handleUserInputKeyUp}
                                placeholder="Message Frends assistant"
                                rows={1}
                                autoFocus
                            />

                            <S.Button
                                ref={sendButtonRef}
                                onClick={handleSendUserInput}
                            >
                                Send
                            </S.Button>
                        </StyledUserInputContainer>
                    </StyledMessagesAndInputPanel>

                    {contextPanelOpen ?
                        <StyledContextPanel>
                            <StyledContextPanelTitleContainer>
                                <h3>The data being sent outside of Frends</h3>

                                <StyledContextPanelCloseButton
                                    title="Close context panel"
                                    onClick={handleCloseContextPanelClick}
                                    disabled={!!parsedContext.error}
                                >
                                    <i className="ci-close_big" />
                                </StyledContextPanelCloseButton>
                            </StyledContextPanelTitleContainer>

                            {parsedContext.error && (
                                <S.ErrorContainer>
                                    <span>{parsedContext.error}</span>
                                </S.ErrorContainer>
                            )}

                            <MultiSelectHideOverFlow
                                menuPlacement="auto"
                                options={allEnvironmentVariables}
                                onChange={handleSelectedEnvironmentVariablesChange}
                                value={Utilities.valuesFromList(allEnvironmentVariables, selectedEnvironmentVariables)}
                                closeMenuOnSelect={false}
                                maxToShow={1}
                                placeholder="Select environment variables"
                                isDisabled={parsedContext.error != null}
                            />

                            <S.ExpressionEditorContainer>
                                <MonacoEditor
                                    onInstantChange={handleContextInputChange}
                                    value={contextInputValue}
                                    mode={ParameterEditorMode.json}
                                    height={650} /* Anything bigger than this and the monaco content is cut off with normal 1200p reso. This is not an optimal solution but works better than without */
                                    key="assistant-modal-context-editor"
                                />
                            </S.ExpressionEditorContainer>
                        </StyledContextPanel>
                        : <span></span>}
                </SplitPane>
            </StyledContentContainer>
        </CustomModal>
    );
};

const StyledModalTitleContainer = styled.div`
    display: flex;
    flex-grow: 1;
    margin-right: 24px;
    justify-content: space-between;
    justify-items: center;
`;

const StyledOpenContextPanelButton = styled(S.Button)`
    padding: 0 15px;
`;

export const StyledContentContainer = styled.div`
    position: relative;
    height: 100vh;
    max-height: 70vh;

    .SplitPane .Resizer {
        display: block;
        margin-top: 20px;
        width: 11px;
        z-index: 1;
        opacity: 0.5;
        cursor: col-resize;

        &:hover {
            -webkit-transition: all .2s ease;
            transition: all .2s ease;
            border-right: 5px solid ${colors.grey_2};
        }

        &.disabled {
            width: 0;
        }
    }
`;
interface IStyledMessagesAndInputPanelProps {
    contextPanelOpen: boolean;
}


export const styledMessageInputPanelBaseStyle = css`
    display: grid;
    grid-template-rows: auto auto;
    height: 100%;
    padding: 0 205px 0 207px;
`;

const StyledMessagesAndInputPanel = styled.div<IStyledMessagesAndInputPanelProps>`
    ${styledMessageInputPanelBaseStyle};
    gap: 72px;
    ${(props) => props.contextPanelOpen && css`
       padding-right: 50px;
       `
    }
`;

const WelcomeFrendIconColor = theme("mode", {
    [ThemeType.light]: colors.black,
    [ThemeType.dark]: colors.grey_4,
});

const WelcomeTextColor = theme("mode", {
    [ThemeType.light]: colors.grey_1,
    [ThemeType.dark]: colors.grey_4,
});

const WelcomeMessageContainer = styled.article`
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;

    span > svg {
        width: 48px;
        height: 48px;

        > path {
            fill: ${WelcomeFrendIconColor};
        }
    }

    p {
        margin: 8px 0 0 0;
        color: ${WelcomeTextColor};
    }
`;

const StyledChatMessagesContainer = styled.article`
    display: flex;
    flex-direction: column;
    padding-right: 8px;
    gap: 34px;
    overflow: auto;
`;

const UserMessageBgColor = theme("mode", {
    [ThemeType.light]: colors.grey_5,
    [ThemeType.dark]: colors.dark3,
});

export const StyledUserMessage = styled.pre`
    font-family: 'Gilroy-Regular';
    max-width: 70%;
    height: fit-content;
    margin: 0 0 0 auto;
    padding: 8px 10px 8px 16px;
    background-color: ${UserMessageBgColor};
    border-radius: 20px;
    white-space: pre-wrap;
    word-break: break-word;
    overflow-wrap: break-word;
    text-wrap: pretty;
`;

interface AssistantMessageProps {
    sessionId: string;
    message: string | TaskAiResponse | null;
    error: string | undefined;
    type: PromptType;
    completionInProgress: boolean;
    elementId: string;
    onDiscardClick?: () => void;
    onUseClick?: () => void;
}

const AssistantMessage: React.FC<AssistantMessageProps> = (props) => {
    const [userSentiment, setUserSentiment] = React.useState<UserSentiment>(UserSentiment.Neutral);
    const taskCompletionData = useTaskCompletionDataGather(props.elementId);
    const handleThumbButtonClick = async (clickedSentiment: UserSentiment) => {
        const nextSentiment = userSentiment !== clickedSentiment ? clickedSentiment : UserSentiment.Neutral;
        setUserSentiment(nextSentiment);

        try {
            await Utilities.putJsonAndParseResponse(`${Utilities.getUrlBase()}api/azureai/prompts/${props.sessionId}/${nextSentiment}`, {});
        } catch (_) {
            Utilities.toastError('Failed to send user sentiment.');
        }
    };

    return (
        <StyledAssistantMessageContainer>
            {props.error && props.error}
            {props.type == PromptType.CodeCompletion && !props.error &&
                <AiMarkdownResponse
                    message={props.message as string}
                    hightLightAllCodeBlocks={true}
                />
            }
            {props.type == PromptType.TaskCompletion && !props.error &&
                <TaskMessage
                    elementId={props.elementId}
                    taskCompletionData={taskCompletionData}
                    taskResponse={props.message as TaskAiResponse | null}
                />
            }

            {props.onDiscardClick && (
                <StyledAssistantMessageActionContainer>
                    <StyledUserSentimentButtonContainer>
                        <StyledUserSentimentThumbButton
                            title="Up vote"
                            dangerouslySetInnerHTML={{ __html: thumbUpIcon('black') }}
                            onClick={() => handleThumbButtonClick(UserSentiment.Positive)}
                            data-voted={userSentiment === UserSentiment.Positive}
                        />
                        <StyledUserSentimentThumbButton
                            title="Down vote"
                            dangerouslySetInnerHTML={{ __html: thumbDownIcon('black') }}
                            onClick={() => handleThumbButtonClick(UserSentiment.Negative)}
                            data-voted={userSentiment === UserSentiment.Negative}
                        />
                    </StyledUserSentimentButtonContainer>

                    <StyledMessageActionButtonContainer>
                        <S.Button
                            variant={S.ButtonType.bordered}
                            onClick={props.onDiscardClick}
                        >
                            Discard
                        </S.Button>

                        <S.Button
                            onClick={props.onUseClick}
                            disabled={!props.onUseClick}
                        >
                            Use
                        </S.Button>
                    </StyledMessageActionButtonContainer>
                </StyledAssistantMessageActionContainer>
            )}
        </StyledAssistantMessageContainer>
    );
};


const TaskMessage: React.FC<{ taskResponse: TaskAiResponse | null, elementId: string, taskCompletionData: ITaskCompletionData | null }> = (props) => {
    if (props.taskResponse == null || props.taskResponse.parameterJson == null || props.taskResponse.explanation == null) {
        return <span>Response from Assistant is not a valid Task configuration. Please try again or contact support@frends.com</span>
    }

    if (props.taskCompletionData == null) {
        return <span>Unable to gather Task metadata for displaying AI response. Please try again or contact support@frends.com</span>
    }

    return <React.Fragment>
        <AiMarkdownResponse
            message={props.taskResponse.explanation}
        />
        <ComplexParameter
            disabled={true}
            isForCompletion={true}
            existingParameters={props.taskResponse.parameterJson}
            passedKey=""
            taskKey={props.taskCompletionData.taskId}
            elementId={props.elementId}
            argumentJson={{ subParameters: props.taskCompletionData.parameterSchema } as IParameterSchema}
            onArrayItemAdd={() => { }}
            onArrayItemRemove={() => { }}
            onChangeArrayToExpression={() => { }}
            onArrayItemIndexChange={() => { }}
        />
    </React.Fragment >
}

export const StyledAssistantMessageContainer = styled.section`
    > pre > div {
        padding: 8px 10px 8px 16px;
        border-radius: 20px !important;
    }
`;

export const StyledAssistantMessageNonCodePre = styled.pre`
    white-space: pre-wrap;
    word-wrap: break-word;
`;

const StyledAssistantMessageActionContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const StyledUserSentimentButtonContainer = styled.div`
    display: flex;
    gap: 12px;
`;

const SentimentThumbButtonColor = theme("mode", {
    [ThemeType.light]: colors.grey_2,
    [ThemeType.dark]: colors.grey_1,
});

const SentimentThumbButtonClickedColor = theme("mode", {
    [ThemeType.light]: colors.black,
    [ThemeType.dark]: colors.grey_4,
});

export const StyledUserSentimentThumbButton = styled.button`
    all: unset;
    outline: revert;
    cursor: pointer;

    > svg > path {
        fill: ${SentimentThumbButtonColor};
    }

    &[data-voted="true"] {
        > svg > path {
            fill: ${SentimentThumbButtonClickedColor};
        }
    }
`;

const StyledMessageActionButtonContainer = styled.div`
    display: flex;
    gap: 16px;
`;

const StyledUserInputContainer = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 16px;

    textarea {
        resize: none;
    }
`;

const ContextPanelBgColor = theme("mode", {
    [ThemeType.light]: colors.grey_5,
    [ThemeType.dark]: colors.grey_1,
});

const ContextPanelBorderColor = theme("mode", {
    [ThemeType.light]: colors.grey_4,
    [ThemeType.dark]: colors.grey_2,
});

export const StyledContextPanel = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    padding: 24px;
    gap: 16px;
    background-color: ${ContextPanelBgColor};
    border-top: 1px solid ${ContextPanelBorderColor};
    border-left: 1px solid ${ContextPanelBorderColor};
    border-radius: 25px 0px 15px 0px;
`;

export const StyledContextPanelTitleContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
`;

export const StyledContextPanelCloseButton = styled(StyledCloseButton)`
    &:disabled {
        cursor: not-allowed;
    }
`;

export enum ChatMessageRole {
    User = 'User',
    Assistant = 'Assistant',
}

export enum PromptType {
    CodeCompletion = 'CodeCompletion',
    TaskCompletion = 'TaskCompletion',
    DocumentationCompletion = 'DocumentationCompletion'
}

export enum UserSentiment {
    Neutral,
    Positive,
    Negative,
}

export interface UserChatMessage {
    message: string;
    role: ChatMessageRole.User;
}

export interface AssistantChatMessage {
    message: string | TaskAiResponse | null;
    role: ChatMessageRole.Assistant
}

export type ChatMessage = UserChatMessage | AssistantChatMessage;

interface ParsedContext {
    context?: IUseAzureAiContextGather;
    error?: string;
}

export interface TaskAiResponse {
    explanation: string,
    parameterJson: IParameters
}

type AssistantModalProps = AssistantModalOwnProps & AssistantModalDispatchProps;

interface AssistantModalOwnProps extends Pick<IModalProps, 'show' | 'onClose'> {
    elementId: string;
    type: PromptType;
}

interface AssistantModalDispatchProps {
    onUseCodeShape: (value: string) => void;
    onUseTaskConfiguration: (parameters: IParameters) => void;
}

const mapDispatchToProps = (dispatch: DispatchType<State>, ownProps: AssistantModalOwnProps): AssistantModalDispatchProps => ({
    onUseCodeShape: (value: string) => dispatch(updateParameter(ownProps.elementId, 'variableExpression', { mode: 'csharp', value })),
    onUseTaskConfiguration: (value: IParameters) => dispatch(setInitialParameters(ownProps.elementId, value)),
});

export default connect(null, mapDispatchToProps)(AssistantModal);